import React, { useState, useEffect } from 'react';
import { useUser } from '../../api/UserContext';
import EditProfile from '../../components/EditProfile/EditProfile';
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Button,
  Progress,
  Tag,
  Input,
  Divider,
  Statistic,
  Space,
  Upload,
  message
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  EditOutlined,
  PlusOutlined,
  GithubOutlined,
  GoldOutlined,
  ZhihuOutlined,
  CodeOutlined,
  WechatOutlined,
  WeiboOutlined,
  FileTextOutlined,
  EyeOutlined,
  LikeOutlined,
  MessageOutlined,
  AppstoreOutlined,
  ClockCircleOutlined,
  IdcardOutlined,
  SearchOutlined
} from '@ant-design/icons';
import './Profile.css';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

const Profile = () => {
  const { user, isLoggedIn } = useUser();
  const [showSkillForm, setShowSkillForm] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [skills, setSkills] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  // 初始化技能数据
  useEffect(() => {
    if (user.techStack && Array.isArray(user.techStack) && user.techStack.length > 0) {
      setSkills(user.techStack);
    } else {
      // 如果没有技能数据，设置默认值
      setSkills([
        { name: 'React', level: 85 },
        { name: 'JavaScript', level: 90 },
        { name: 'CSS/HTML', level: 95 },
        { name: 'Node.js', level: 75 }
      ]);
    }
  }, [user.techStack]);

  // 添加技能函数
  const handleAddSkill = (value) => {
    if (value && value.trim()) {
      const newSkillObj = {
        name: value.trim(),
        level: 50 // 默认技能水平
      };
      setSkills([...skills, newSkillObj]);
      setNewSkill('');
      setShowSkillForm(false);
      messageApi.success(`技能 "${value.trim()}" 添加成功`);
    } else {
      messageApi.warning('请输入技能名称');
    }
  };

  // 搜索框的 onSearch 处理函数
  const handleSearch = (value) => {
    handleAddSkill(value);
  };

  // 上传前验证
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      messageApi.error('只能上传 JPG/PNG 文件!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      messageApi.error('图片必须小于 2MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  // 处理上传
  const handleUploadChange = (info) => {
    if (info.file.status === 'done') {
      messageApi.success(`${info.file.name} 上传成功`);
    } else if (info.file.status === 'error') {
      messageApi.error(`${info.file.name} 上传失败`);
    }
  };

  const infoCards = [
    { 
      icon: <MailOutlined />, 
      title: '邮箱', 
      content: user.email || '未设置',
      color: '#1890ff'
    },
    { 
      icon: <IdcardOutlined />,
      title: '职位', 
      content: user.position || '未设置',
      color: '#52c41a'
    },
    { 
      icon: <EnvironmentOutlined />, 
      title: '位置', 
      content: user.location || '未设置',
      color: '#fa8c16'
    },
    { 
      icon: <CalendarOutlined />, 
      title: '加入时间', 
      content: user.joinDate || '2023-01-01',
      color: '#722ed1'
    }
  ];

  const blogStats = [
    { icon: <FileTextOutlined />, value: 42, label: '发布文章' },
    { icon: <EyeOutlined />, value: 3280, label: '总阅读量' },
    { icon: <LikeOutlined />, value: 156, label: '获得点赞' },
    { icon: <MessageOutlined />, value: 89, label: '评论数量' },
    { icon: <AppstoreOutlined />, value: 18, label: '文章分类' },
    { icon: <ClockCircleOutlined />, value: 365, label: '创作天数' }
  ];

  const socialLinks = [
    { icon: <GithubOutlined />, label: 'GitHub', url: '#' },
    { icon: <GoldOutlined />, label: '掘金', url: '#' },
    { icon: <ZhihuOutlined />, label: '知乎', url: '#' },
    { icon: <CodeOutlined />, label: 'CSDN', url: '#' },
    { icon: <WechatOutlined />, label: '微信公众号', url: '#' }
  ];

  // 获取用户技能标签（确保是数组）
  const getUserSkills = () => {
    if (!user.skills) return [];
    if (Array.isArray(user.skills)) return user.skills;
    if (typeof user.skills === 'string') return [user.skills];
    return [];
  };

  const userSkills = getUserSkills();

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* 头部信息 */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
          // 修复: 使用 styles 替代 bodyStyle
          styles={{ body: { padding: 24 } }}
        >
          <Row gutter={[32, 32]} align="middle">
            <Col xs={24} md={6} style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Upload
                  name="avatar"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={handleUploadChange}
                  customRequest={({ onSuccess }) => {
                    setTimeout(() => onSuccess('ok'), 0);
                  }}
                >
                  <Avatar
                    size={120}
                    src={user.avatar}
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: user.avatarColor || '#4a90e2',
                      fontSize: 48,
                      border: '4px solid #fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      cursor: 'pointer'
                    }}
                  />
                </Upload>
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: '#1890ff',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #fff',
                  cursor: 'pointer'
                }}>
                  <EditOutlined style={{ color: '#fff', fontSize: 16 }} />
                </div>
              </div>
            </Col>
            
            <Col xs={24} md={18}>
              <Title level={2} style={{ marginBottom: 8, fontWeight: 600 }}>
                Hi，我是 {user.name}！
              </Title>
              <Paragraph 
                style={{ 
                  color: '#666', 
                  marginBottom: 8,
                  fontSize: 16
                }}
              >
                {user.bio || '这个人很懒，还没有写简介...'}
              </Paragraph>
              <Text type="secondary" style={{ fontSize: 14 }}>
                探索我的博客，发现技术世界的精彩！
              </Text>
            </Col>
          </Row>
        </Card>

        {/* 编辑按钮 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Button
            type="primary"
            size="large"
            icon={<EditOutlined />}
            onClick={() => setShowEditModal(true)}
            style={{
              padding: '8px 32px',
              height: 'auto',
              borderRadius: 6,
              fontSize: 16
            }}
          >
            编辑个人信息
          </Button>
        </div>

        <Row gutter={[24, 24]}>
          {/* 左侧内容 */}
          <Col xs={24} lg={16}>
            {/* 个人信息卡片 */}
            <Card
              title="个人信息"
              style={{ marginBottom: 24 }}
              // 修复: 使用 styles.header 替代 headStyle
              styles={{ 
                header: { fontSize: 18, fontWeight: 600 },
                body: { padding: 16 }
              }}
            >
              <Row gutter={[16, 16]}>
                {infoCards.map((item, index) => (
                  <Col xs={24} sm={12} key={index}>
                    <Card
                      size="small"
                      style={{ borderRadius: 8 }}
                      // 修复: 使用 styles 替代 bodyStyle
                      styles={{ body: { padding: 16 } }}
                    >
                      <Space align="center" style={{ width: '100%' }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {React.cloneElement(item.icon, { 
                            style: { color: '#fff', fontSize: 18 }
                          })}
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.title}
                          </Text>
                          <div style={{ fontWeight: 500, fontSize: 16 }}>
                            {item.content}
                          </div>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* 技术栈 */}
            <Card
              title="技术栈"
              style={{ marginBottom: 24 }}
              // 修复: 使用 styles.header 替代 headStyle
              styles={{ 
                header: { fontSize: 18, fontWeight: 600 }
              }}
              extra={
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setShowSkillForm(!showSkillForm)}
                >
                  {showSkillForm ? '取消' : '添加技能'}
                </Button>
              }
            >
              {showSkillForm && (
                <div style={{ marginBottom: 24 }}>
                  <Search
                    placeholder="请输入新技能名称"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onSearch={handleSearch}
                    enterButton={
                      <Button type="primary" icon={<PlusOutlined />}>
                        添加
                      </Button>
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              )}
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {skills.map((tech, index) => (
                  <div key={index}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: 8
                    }}>
                      <Text strong style={{ minWidth: 100 }}>
                        {tech.name}
                      </Text>
                      <Text type="secondary">{tech.level}%</Text>
                    </div>
                    <Progress 
                      percent={tech.level} 
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                      showInfo={false}
                    />
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          {/* 右侧内容 */}
          <Col xs={24} lg={8}>
            {/* 兴趣爱好 */}
            <Card
              title="兴趣爱好"
              style={{ marginBottom: 24 }}
              // 修复: 使用 styles.header 替代 headStyle
              styles={{ 
                header: { fontSize: 18, fontWeight: 600 }
              }}
            >
              <Space size={[8, 8]} wrap>
                {userSkills.map((skill, index) => (
                  <Tag 
                    key={index}
                    color="blue"
                    style={{ borderRadius: 15, padding: '4px 12px' }}
                  >
                    {skill}
                  </Tag>
                ))}
                <Tag
                  icon={<PlusOutlined />}
                  style={{ 
                    cursor: 'pointer',
                    borderRadius: 15,
                    padding: '4px 12px'
                  }}
                  onClick={() => messageApi.info('添加新兴趣功能待实现')}
                >
                  添加
                </Tag>
              </Space>
            </Card>

            {/* 社交媒体 */}
            <Card
              title="社交媒体"
              style={{ marginBottom: 24 }}
              // 修复: 使用 styles.header 替代 headStyle
              styles={{ 
                header: { fontSize: 18, fontWeight: 600 }
              }}
            >
              {/* 修复: 使用 orientation 替代 direction */}
              <Space orientation="vertical" style={{ width: '100%' }}>
                {socialLinks.map((social, index) => (
                  <Button
                    key={index}
                    type="text"
                    icon={social.icon}
                    block
                    style={{ 
                      textAlign: 'left',
                      height: 48,
                      borderRadius: 6
                    }}
                    onClick={() => window.open(social.url, '_blank')}
                  >
                    {social.label}
                  </Button>
                ))}
                
                <Divider style={{ margin: '12px 0' }} />
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  borderRadius: 6,
                  border: '1px solid #f0f0f0'
                }}>
                  <WeiboOutlined style={{ marginRight: 8, color: '#ff6b6b' }} />
                  <Text style={{ flex: 1 }}>{user.socialMedia || '未设置微博'}</Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* 博客统计 */}
        <Card
          title="博客统计"
          style={{ marginTop: 24 }}
          // 修复: 使用 styles.header 替代 headStyle
          styles={{ 
            header: { fontSize: 18, fontWeight: 600 }
          }}
        >
          <Row gutter={[16, 16]}>
            {blogStats.map((stat, index) => (
              <Col xs={12} sm={8} md={4} key={index}>
                <Card
                  style={{ 
                    textAlign: 'center',
                    borderRadius: 8,
                    height: '100%'
                  }}
                  // 修复: 使用 styles 替代 bodyStyle
                  styles={{ body: { padding: 16 } }}
                >
                  <div style={{ 
                    fontSize: 24, 
                    color: '#1890ff',
                    marginBottom: 8
                  }}>
                    {stat.icon}
                  </div>
                  {/* 修复: 使用 styles 替代 valueStyle */}
                  <Statistic
                    value={stat.value}
                    styles={{ 
                      content: { fontSize: '24px', fontWeight: 600 }
                    }}
                  />
                  <Text type="secondary">{stat.label}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      </div>

      {/* 编辑个人信息模态框 - 直接渲染 EditProfile 组件 */}
      {showEditModal && (
        <EditProfile onClose={() => setShowEditModal(false)} />
      )}
    </>
  );
};

export default Profile;